import React, { useRef } from 'react';
import { Image } from 'primereact/image';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';

interface ImageSlotProps {
    imageUrl?: string;
    onImageSelect: (file: File) => void;
    onImageRemove?: () => void;
    accept?: string;
    disabled?: boolean;
    width?: string;
    height?: string;
    className?: string;
    placeholder?: string;
}

export const ImageSlot: React.FC<ImageSlotProps> = ({
    imageUrl,
    onImageSelect,
    onImageRemove,
    accept = "image/*",
    disabled = false,
    width = "150px",
    height = "120px",
    className = "",
    placeholder = "Thêm ảnh"
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageRef = useRef<any>(null);

    const handleImageClick = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            onImageSelect(file);
        }
        // Clear input để có thể chọn lại cùng file
        event.target.value = '';
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onImageRemove) {
            onImageRemove();
        }
    };

    const handleViewDetail = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (imageUrl) {
            if (accept.includes('video')) {
                // Mở video trong tab mới
                window.open(imageUrl, '_blank');
            } else {
                // Trigger preview của PrimeReact Image
                if (imageRef.current) {
                    imageRef.current.show();
                }
            }
        }
    };

    return (
        <div className={`relative ${className}`} style={{ width, height }}>
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={disabled}
            />

            <Card
                className={`image-slot-card ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{
                    width: '100%',
                    height: '100%',
                    padding: 0,
                    border: '2px dashed #e0e0e0',
                    borderRadius: '8px'
                }}
                pt={{
                    body: { style: { padding: 0, height: '100%' } },
                    content: { style: { padding: 0, height: '100%' } }
                }}
            >
                <div className="w-full h-full overflow-hidden">
                    {imageUrl ? (
                        <div
                            className="relative w-full h-full"
                            onClick={(e) => e.stopPropagation()} // Ngăn click event bubble lên Card
                        >
                            {accept.includes('video') ? (
                                <video
                                    src={imageUrl}
                                    className="w-full h-full border-round"
                                    style={{ objectFit: 'cover' }}
                                    controls
                                    onClick={(e) => e.stopPropagation()}
                                    onError={(e: any) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : (
                                <Image
                                    ref={imageRef}
                                    src={imageUrl}
                                    alt="Preview"
                                    className="w-full h-full border-round"
                                    preview
                                    onClick={(e) => e.stopPropagation()}
                                    imageStyle={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '8px'
                                    }}
                                    onError={(e: any) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            )}

                            {/* Overlay khi hover */}
                            <div
                                className="absolute image-slot-overlay opacity-0 flex align-items-center justify-content-center border-round"
                                onClick={(e) => e.stopPropagation()} // Ngăn click event bubble lên Card
                                style={{
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    borderRadius: '8px'
                                }}
                            >
                                <div className="flex gap-2">
                                    <Button
                                        icon="pi pi-eye"
                                        size="small"
                                        rounded
                                        severity="secondary"
                                        tooltip="Xem chi tiết"
                                        onClick={handleViewDetail}
                                    />
                                    <Button
                                        icon="pi pi-pencil"
                                        size="small"
                                        rounded
                                        severity="info"
                                        tooltip="Thay đổi ảnh"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleImageClick();
                                        }}
                                    />
                                    {onImageRemove && (
                                        <Button
                                            icon="pi pi-times"
                                            size="small"
                                            rounded
                                            severity="danger"
                                            tooltip="Xóa ảnh"
                                            onClick={handleRemove}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="w-full h-full flex flex-column justify-content-center align-items-center cursor-pointer"
                            onClick={handleImageClick}
                        >
                            <i
                                className={`pi ${accept.includes('video') ? 'pi-video' : 'pi-plus'} text-gray-400 mb-2`}
                                style={{ fontSize: '2rem' }}
                            ></i>
                            <div className="text-sm text-gray-500 text-center">
                                {placeholder}
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};
